<template>
  <div class="fr">
    <div class="fr__layout">
      <v-card class="fr__card" outlined>
      <!-- ---------------------------------------------------------- filters -->
      <div class="fr__filters">
        <v-select
          v-model="filters.companyId"
          class="fr__filter fr__filter--company"
          :items="companyItems"
          item-text="name"
          item-value="id"
          label="Company"
          placeholder="All companies"
          clearable
          outlined
          dense
          hide-details
          @change="reload" />

        <div class="fr__ratings" role="group" aria-label="Filter by rating">
          <span class="fr__ratings-label">Rating</span>
          <v-chip
            v-for="n in maxRating"
            :key="n"
            class="fr__chip"
            small
            :outlined="!isRatingOn(n)"
            :color="isRatingOn(n) ? 'primary' : undefined"
            :dark="isRatingOn(n)"
            :aria-pressed="String(isRatingOn(n))"
            role="button"
            tabindex="0"
            @click="toggleRating(n)"
            @keydown.enter.prevent="toggleRating(n)"
            @keydown.space.prevent="toggleRating(n)">
            {{ n }}
            <v-icon size="13" class="fr__chip-star" :class="{ 'fr__chip-star--on': isRatingOn(n) }">
              mdi-star
            </v-icon>
          </v-chip>
        </div>

        <v-menu
          v-model="fromMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px">
          <template #activator="{ on, attrs }">
            <v-text-field
              :value="filters.createdFrom"
              class="fr__filter fr__filter--date"
              label="From"
              prepend-inner-icon="mdi-calendar-start"
              readonly
              clearable
              outlined
              dense
              hide-details
              v-bind="attrs"
              v-on="on"
              @click:clear="clearDate('createdFrom')" />
          </template>
          <v-date-picker
            v-model="filters.createdFrom"
            color="primary"
            :max="filters.createdTo || undefined"
            @input="onDatePicked('from')" />
        </v-menu>

        <v-menu
          v-model="toMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px">
          <template #activator="{ on, attrs }">
            <v-text-field
              :value="filters.createdTo"
              class="fr__filter fr__filter--date"
              label="To"
              prepend-inner-icon="mdi-calendar-end"
              readonly
              clearable
              outlined
              dense
              hide-details
              v-bind="attrs"
              v-on="on"
              @click:clear="clearDate('createdTo')" />
          </template>
          <v-date-picker
            v-model="filters.createdTo"
            color="primary"
            :min="filters.createdFrom || undefined"
            @input="onDatePicked('to')" />
        </v-menu>

        <v-spacer />

        <v-btn v-if="hasActiveFilters" class="fr__clear" text small @click="clearFilters">
          <v-icon left size="16">mdi-filter-remove-outline</v-icon>
          Clear filters
        </v-btn>
      </div>

      <v-divider />

      <!-- ------------------------------------------------------------ table -->
      <v-data-table
        class="fr__table"
        must-sort
        :headers="headers"
        :items="submissions"
        :options.sync="options"
        :server-items-length="total"
        :loading="loading"
        :footer-props="footerProps"
        item-key="id"
        :item-class="rowClass"
        @dblclick:row="onRowDoubleClick">
        <template #[`item.createDate`]="{ item }">
          <span class="fr__date">{{ item.createDate | formatDate }}</span>
        </template>

        <template #[`item.userDisplayName`]="{ item }">
          <div class="fr__user">
            <span class="fr__user-name" :class="{ 'fr__user-name--gone': !item.userDisplayName }">
              {{ item.userDisplayName || 'Former user' }}
            </span>
            <span v-if="item.userEmail" class="fr__user-email">{{ item.userEmail }}</span>
          </div>
        </template>

        <template #[`item.companyName`]="{ item }">
          <span :class="{ 'fr__muted': !item.companyName }">{{ item.companyName || '—' }}</span>
        </template>

        <template #[`item.rating`]="{ item }">
          <span class="fr__rating">
            <span class="fr__rating-num">{{ item.rating }}</span>
            <v-icon size="15" class="fr__rating-star">mdi-star</v-icon>
          </span>
        </template>

        <template #[`item.comment`]="{ item }">
          <p v-if="item.comment" class="fr__comment">{{ item.comment }}</p>
          <span v-else class="fr__comment fr__comment--none">No comment</span>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn
            class="fr__open"
            icon
            :aria-label="`Open submission from ${item.userDisplayName || 'former user'}`"
            @click.stop="openPanel(item)">
            <v-icon size="18">mdi-open-in-new</v-icon>
          </v-btn>
        </template>

        <template #no-data>
          <div class="fr__empty">
            <v-icon size="30" class="fr__empty-icon">
              {{ hasActiveFilters ? 'mdi-filter-off-outline' : 'mdi-star-outline' }}
            </v-icon>
            <p class="fr__empty-title">
              {{ hasActiveFilters ? 'No submissions match these filters' : 'No feedback yet' }}
            </p>
            <p class="fr__empty-body">
              {{
                hasActiveFilters
                  ? 'Try widening the date range, clearing the rating selection, or choosing a different company.'
                  : 'Nothing has been submitted so far. Submissions appear here as soon as users respond.'
              }}
            </p>
            <v-btn v-if="hasActiveFilters" text small @click="clearFilters">Clear filters</v-btn>
          </div>
        </template>
      </v-data-table>
      </v-card>

      <!--
        An inline panel beside the table, matching the register table's
        row-details panel - not an overlay drawer. A fixed drawer starts at y=0
        and slides under the app's top bar, which clipped the heading.
      -->
      <transition name="fr-panel">
        <aside
          v-if="selected"
          ref="panel"
          class="fr-panel"
          role="region"
          aria-labelledby="feedback-panel-title"
          tabindex="-1"
          @keydown.esc.stop="closePanel">
          <header class="fr-panel__header">
            <div class="fr-panel__mark" aria-hidden="true">
              <v-icon color="primary" size="21">mdi-star-outline</v-icon>
            </div>
            <div class="fr-panel__heading">
              <span>Feedback submission</span>
              <h2 id="feedback-panel-title">{{ selected.userDisplayName || 'Former user' }}</h2>
              <p>{{ selected.companyName || 'Company unavailable' }}</p>
            </div>
            <v-btn
              icon
              small
              class="fr-panel__close"
              aria-label="Close submission details"
              title="Close submission details"
              @click="closePanel">
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </header>

          <!--
            Stepping through the list from inside the panel. Without it, reading
            ten 1-star submissions costs ten open/close cycles and loses your
            place each time - which is the job this screen exists for.
          -->
          <div class="fr-panel__nav">
            <v-btn icon small aria-label="Previous submission" :disabled="!hasPrevious" @click="step(-1)">
              <v-icon size="20">mdi-chevron-up</v-icon>
            </v-btn>
            <v-btn icon small aria-label="Next submission" :disabled="!hasNext" @click="step(1)">
              <v-icon size="20">mdi-chevron-down</v-icon>
            </v-btn>
            <span class="fr-panel__position" aria-live="polite">
              {{ selectedIndex + 1 }} of {{ submissions.length }} on this page
            </span>
          </div>

          <div class="fr-panel__body">
            <dl class="fr-panel__facts">
              <div>
                <dt>Rating</dt>
                <dd>
                  <span class="fr__rating">
                    <span class="fr__rating-num">{{ selected.rating }}</span>
                    <v-icon size="16" class="fr__rating-star">mdi-star</v-icon>
                    <span class="fr-panel__rating-of">of {{ maxRating }}</span>
                  </span>
                </dd>
              </div>
              <div>
                <dt>Submitted</dt>
                <dd>{{ formatDateTime(selected.createDate) }}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd :class="{ 'fr__muted': !selected.userEmail }">
                  {{ selected.userEmail || 'Account deleted — email unavailable' }}
                </dd>
              </div>
            </dl>

            <div class="fr-panel__comment-block">
              <span class="fr-panel__comment-label">Comment</span>
              <p v-if="selected.comment" class="fr-panel__comment">{{ selected.comment }}</p>
              <p v-else class="fr-panel__comment fr-panel__comment--none">
                This user rated AVA without leaving a comment.
              </p>
            </div>
          </div>

          <div class="fr-panel__footer">
            <v-btn
              v-if="selected.userEmail"
              color="primary"
              depressed
              block
              :href="`mailto:${selected.userEmail}`">
              <v-icon left size="18">mdi-email-outline</v-icon>
              Email this user
            </v-btn>
            <v-btn v-else block disabled>Email unavailable</v-btn>
          </div>
        </aside>
      </transition>
    </div>

    <p class="fr__hint">Double-click a row, or use the open icon, to read the full submission.</p>
  </div>
</template>

<script>
  import {
    FEEDBACK_RATING,
    FEEDBACK_RESULTS_PAGE_SIZES,
    FEEDBACK_SORT_MAP,
  } from '@Feedback/constants/feedbackConstants';

  export default {
    name: 'FeedbackResultsTable',

    data() {
      return {
        filters: {
          companyId: null,
          ratings: [],
          createdFrom: null,
          createdTo: null,
        },
        options: {
          page: 1,
          itemsPerPage: 25,
          sortBy: ['createDate'],
          sortDesc: [true],
        },
        fromMenu: false,
        toMenu: false,
        /** The submission shown in the side panel; null closes it. */
        selected: null,
        returnFocusEl: null,
      };
    },

    computed: {
      submissions() {
        return this.$store.state.FEEDBACK_MANAGEMENT.submissions;
      },
      total() {
        return this.$store.state.FEEDBACK_MANAGEMENT.submissionsTotal;
      },
      loading() {
        return this.$store.state.FEEDBACK_MANAGEMENT.submissionsLoading;
      },
      companyItems() {
        return this.$store.state.FEEDBACK_MANAGEMENT.companies;
      },
      maxRating() {
        return FEEDBACK_RATING.MAX;
      },
      headers() {
        return [
          { text: 'Submitted', value: 'createDate', width: 130 },
          { text: 'User', value: 'userDisplayName', width: 240 },
          { text: 'Company', value: 'companyName', width: 180 },
          { text: 'Rating', value: 'rating', width: 100 },
          { text: 'Comment', value: 'comment', sortable: false },
          { text: '', value: 'actions', sortable: false, width: 56, align: 'end' },
        ];
      },
      footerProps() {
        return {
          showFirstLastPage: true,
          firstIcon: 'mdi-chevron-double-left',
          lastIcon: 'mdi-chevron-double-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
          itemsPerPageText: 'Row/Page',
          // Deliberately narrower than the repo default, which offers up to 500
          // and "All": the API hard-caps a page at 200, so those options would
          // silently return fewer rows than they promise.
          itemsPerPageOptions: FEEDBACK_RESULTS_PAGE_SIZES,
        };
      },
      hasActiveFilters() {
        const f = this.filters;
        return !!(f.companyId || f.ratings.length || f.createdFrom || f.createdTo);
      },
      selectedIndex() {
        if (!this.selected) return -1;
        return this.submissions.findIndex((s) => s.id === this.selected.id);
      },
      hasPrevious() {
        return this.selectedIndex > 0;
      },
      hasNext() {
        return this.selectedIndex > -1 && this.selectedIndex < this.submissions.length - 1;
      },
    },

    watch: {
      options: {
        deep: true,
        handler() {
          this.load();
        },
      },
      submissions() {
        // The open row may not exist in the page that just arrived (a filter,
        // sort or page change). Close rather than leave a panel describing a row
        // the table no longer shows.
        if (this.selected && this.selectedIndex === -1) this.selected = null;
      },
    },

    created() {
      this.$store.dispatch('FEEDBACK_MANAGEMENT/LOAD_COMPANIES');
      this.load();
    },

    methods: {
      load() {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options;
        this.$store.dispatch('FEEDBACK_MANAGEMENT/LOAD_SUBMISSIONS', {
          page,
          itemsPerPage,
          companyId: this.filters.companyId || undefined,
          // Repeated query keys (?ratings=4&ratings=5); httpService already
          // serializes arrays that way, which is what ASP.NET Core binding wants.
          ratings: this.filters.ratings.length ? this.filters.ratings : undefined,
          createdFrom: this.toStartOfDay(this.filters.createdFrom),
          // Inclusive: an admin picking a day means the whole of that day, and
          // sending midnight would silently exclude everything submitted in it.
          createdTo: this.toEndOfDay(this.filters.createdTo),
          sortBy: FEEDBACK_SORT_MAP[sortBy && sortBy[0]] || 'CreateDate',
          sortDesc: sortDesc && sortDesc.length ? sortDesc[0] : true,
        });
      },

      reload() {
        // Any filter change invalidates the current page number. Setting page
        // triggers the options watcher, so only load directly when already on 1.
        if (this.options.page === 1) this.load();
        else this.options.page = 1;
      },

      isRatingOn(n) {
        return this.filters.ratings.includes(n);
      },

      toggleRating(n) {
        const i = this.filters.ratings.indexOf(n);
        if (i === -1) this.filters.ratings.push(n);
        else this.filters.ratings.splice(i, 1);
        this.reload();
      },

      onDatePicked(which) {
        if (which === 'from') this.fromMenu = false;
        else this.toMenu = false;
        this.reload();
      },

      clearDate(key) {
        this.filters[key] = null;
        this.reload();
      },

      clearFilters() {
        this.filters = { companyId: null, ratings: [], createdFrom: null, createdTo: null };
        this.reload();
      },

      toStartOfDay(day) {
        return day ? new Date(day + 'T00:00:00').toISOString() : undefined;
      },

      toEndOfDay(day) {
        return day ? new Date(day + 'T23:59:59.999').toISOString() : undefined;
      },

      // Date+time has no shared helper (the global `formatDate` filter is
       // date-only), so this one stays local.
      formatDateTime(value) {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
      },

      rowClass(item) {
        return this.selected && this.selected.id === item.id ? 'fr__row--open' : '';
      },

      onRowDoubleClick(event, { item }) {
        // A double-click otherwise leaves a text selection highlighted behind the panel.
        const selection = window.getSelection();
        if (selection) selection.removeAllRanges();
        this.openPanel(item);
      },

      openPanel(item) {
        // Capture the return target only on first open; stepping between rows
        // must not overwrite it with a button inside the panel itself.
        if (!this.selected) this.returnFocusEl = document.activeElement;
        this.selected = item;
        this.$nextTick(() => {
          const panel = this.$refs.panel;
          if (panel && typeof panel.focus === 'function') panel.focus();
        });
      },

      closePanel() {
        this.selected = null;
        this.restoreFocus();
      },

      step(delta) {
        const next = this.submissions[this.selectedIndex + delta];
        if (next) this.selected = next;
      },

      restoreFocus() {
        const el = this.returnFocusEl;
        this.returnFocusEl = null;
        // The row may have been re-rendered away while the panel was open.
        if (!el || !el.isConnected || typeof el.focus !== 'function') return;
        this.$nextTick(() => el.focus());
      },
    },
  };
</script>

<style lang="scss" scoped>
  // Fills the height it is given and never scrolls itself - the table scrolls
  // inside its own card instead, so filters, header row and pager stay put.
  .fr {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  // The table and the detail panel sit side by side, the way the register table
  // does it. An overlay drawer was wrong here: fixed positioning starts at y=0,
  // so its heading slid under the app's top bar.
  .fr__layout {
    display: flex;
    align-items: stretch;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
  }

  // Deliberately NOT the global `custom-table` helper: that one is built for
  // pages that fill the viewport (height: -webkit-fill-available, display: flex)
  // and collapses this table into a short, separately-scrolling box inside a tab.
  .fr__card {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  }

  .fr__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
  }

  .fr__filter--company {
    max-width: 240px;
  }

  .fr__filter--date {
    max-width: 168px;
  }

  .fr__ratings {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .fr__ratings-label {
    font-size: 12px;
    color: var(--v-text-base);
    margin-right: 2px;
  }

  .fr__chip {
    cursor: pointer;
    font-variant-numeric: tabular-nums;
  }

  .fr__chip-star {
    margin-left: 3px;
    color: var(--ds-color-rating);
  }

  .fr__chip-star--on {
    color: #fff;
  }

  // ------------------------------------------------------------------- table
  // Written out rather than reusing the global `table-sticky custom-table` pair:
  // those also restyle the footer and force `padding: 0 6px` onto every cell,
  // which would flatten the row height the comment column needs.
  .fr__table {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;

    ::v-deep .v-data-table__wrapper {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
    }

    ::v-deep .v-data-table-header th {
      position: sticky;
      top: 0;
      z-index: 2;
      background: var(--v-bg2-base);
      white-space: nowrap;
    }

    ::v-deep .v-data-footer {
      flex: 0 0 auto;
    }
  }

  .fr__table ::v-deep tbody td {
    height: 56px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .fr__table ::v-deep tbody tr.fr__row--open {
    background: var(--ds-surface-active, rgba(3, 98, 76, 0.08));
  }

  .fr__date {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .fr__user {
    display: flex;
    flex-direction: column;
    line-height: 1.35;
  }

  .fr__user-name {
    font-weight: 600;
  }

  .fr__user-name--gone {
    font-style: italic;
    font-weight: 400;
    color: var(--v-text-base);
  }

  // --v-text-base is the only theme-aware text token; hierarchy against the name
  // above therefore comes from size and opacity, not a second colour.
  .fr__user-email {
    font-size: 12px;
    color: var(--v-text-base);
    opacity: 0.72;
  }

  .fr__muted {
    color: var(--v-text-base);
    opacity: 0.72;
  }

  .fr__rating {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-variant-numeric: tabular-nums;
  }

  .fr__rating-num {
    font-weight: 600;
  }

  .fr__rating-star {
    color: var(--ds-color-rating);
  }

  // Two lines, not one: at this column width one line shows only a fraction of a
  // typical comment, and the row is already two lines tall for the user cell.
  .fr__comment {
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  // Styled as content, not as a disabled control: "no comment" is a fact about
  // the submission, not an unavailable action.
  .fr__comment--none {
    font-style: italic;
    color: var(--v-text-base);
  }

  .fr__open {
    width: 36px;
    height: 36px;
  }

  .fr__hint {
    flex: 0 0 auto;
    font-size: 12px;
    color: var(--v-text-base);
    margin: 10px 2px 0;
  }

  .fr__empty {
    padding: 44px 16px;
    text-align: center;
  }

  .fr__empty-icon {
    opacity: 0.45;
    margin-bottom: 10px;
  }

  .fr__empty-title {
    font-weight: 600;
    margin: 0;
  }

  .fr__empty-body {
    font-size: 13px;
    color: var(--v-text-base);
    margin: 4px auto 12px;
    max-width: 46ch;
  }

  // ------------------------------------------------------- side detail panel
  // Mirrors .row-details-panel in Registers/views/RegisterTable.vue.
  .fr-panel {
    display: flex;
    width: 392px;
    min-width: 392px;
    max-width: 392px;
    min-height: 0;
    flex: 0 0 392px;
    flex-direction: column;
    align-self: stretch;
    margin-left: 12px;
    border: 1px solid var(--ds-border-default);
    border-radius: var(--ds-radius-md);
    color: var(--v-text-base);
    background: var(--v-bg2-base);
    box-shadow: var(--ds-shadow-2);
    overflow: hidden;

    &:focus-visible {
      outline: var(--ds-focus-ring-width, 2px) solid var(--v-primary-base);
      outline-offset: -2px;
    }

    &__header {
      display: grid;
      min-height: 88px;
      flex: 0 0 auto;
      grid-template-columns: 40px minmax(0, 1fr) 32px;
      align-items: start;
      gap: var(--ds-space-sm, 8px);
      padding: var(--ds-space-md, 16px);
      border-bottom: 1px solid var(--ds-border-subtle);
      background: var(--v-bg3-base);
    }

    &__mark {
      display: flex;
      width: 40px;
      height: 40px;
      align-items: center;
      justify-content: center;
      border-radius: var(--ds-radius-md);
      background: rgba(3, 98, 76, 0.1);
    }

    &__heading {
      display: flex;
      min-width: 0;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;

      span {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--v-text-base);
      }

      h2 {
        margin: 0;
        font-family: var(--ds-font-family-heading, 'Poppins'), sans-serif;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.3;
        word-break: break-word;
      }

      p {
        margin: 0;
        font-size: 13px;
        color: var(--v-text-base);
      }
    }

    &__nav {
      display: flex;
      align-items: center;
      gap: 4px;
      flex: 0 0 auto;
      padding: 10px 16px;
      border-bottom: 1px solid var(--ds-border-subtle);
    }

    &__position {
      font-size: 12px;
      color: var(--v-text-base);
      margin-left: 6px;
    }

    &__body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      padding: 16px;
    }

    &__facts {
      margin: 0 0 20px;

      > div {
        display: flex;
        align-items: baseline;
        gap: 12px;
        padding: 8px 0;
        border-bottom: 1px solid var(--ds-border-subtle);
      }

      dt {
        flex: 0 0 88px;
        font-size: 12px;
        color: var(--v-text-base);
      }

      dd {
        margin: 0;
        min-width: 0;
        font-size: 13px;
        word-break: break-word;
      }
    }

    &__rating-of {
      font-size: 12px;
      color: var(--v-text-base);
      margin-left: 2px;
    }

    &__comment-label {
      display: block;
      font-size: 12px;
      color: var(--v-text-base);
      margin-bottom: 6px;
    }

    // The whole reason the panel exists: a long comment gets room to breathe
    // instead of being clipped to two lines in a cell.
    &__comment {
      font-size: 14px;
      line-height: 1.6;
      white-space: pre-wrap;
      margin: 0;
    }

    &__comment--none {
      font-style: italic;
      color: var(--v-text-base);
    }

    &__footer {
      flex: 0 0 auto;
      padding: 12px 16px 16px;
      border-top: 1px solid var(--ds-border-subtle);
    }
  }

  .fr-panel-enter-active,
  .fr-panel-leave-active {
    transition: opacity var(--ds-transition-default), transform var(--ds-transition-default);
  }

  .fr-panel-enter,
  .fr-panel-leave-to {
    opacity: 0;
    transform: translateX(12px);
  }

  @media (prefers-reduced-motion: reduce) {
    .fr-panel-enter-active,
    .fr-panel-leave-active {
      transition-duration: 0.01ms !important;
    }
  }
</style>
